import { useMemo } from 'react'
import {
  HOUR,
  isEmpty,
  messageFromError,
  type APIError,
  type ImportData,
  type ImportListInput,
  type ImportListSuccess,
} from '@terros/common'
import { useInfiniteQuery, type InfiniteData, type QueryKey } from '@tanstack/react-query'
import { importsKey } from '../keys'
import { useServerRequest } from '../api'
import { type GetInfiniteHookResponse } from '../infiniteQuery'

const DEFAULT_PAGE_SIZE = 30

export function useImports(input: ImportListInput = {}): GetInfiniteHookResponse<ImportData> {
  const serverRequest = useServerRequest<ImportListInput, ImportListSuccess>('/import/list')
  const pageInput: ImportListInput = { ...input, size: input.size ?? DEFAULT_PAGE_SIZE }

  const query = useInfiniteQuery<
    ImportListSuccess,
    APIError,
    InfiniteData<ImportListSuccess>,
    QueryKey,
    ImportListInput
  >({
    queryKey: importsKey(pageInput),
    queryFn: async ({ pageParam }) => serverRequest(pageParam),
    initialPageParam: pageInput,
    getNextPageParam: (lastPage) => {
      if (isEmpty(lastPage.cursor)) return
      return { ...pageInput, cursor: lastPage.cursor }
    },
    staleTime: HOUR,
    gcTime: 6 * HOUR,
    refetchOnMount: true,
  })

  const data = useMemo(() => query.data?.pages.flatMap((page) => page.logs) ?? [], [query.data])

  return {
    ...query,
    error: messageFromError(query.error),
    data,
  }
}
